import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Users, MessageSquare, Upload, Download, Eye } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

// Import viewers
import ProjectInfoViewer from '@/components/shared/ProjectInfoViewer';
import ProjectFilesViewer from '@/components/shared/ProjectFilesViewer';
import CommentsViewer from '@/components/shared/CommentsViewer';

const SharedProjectView = () => {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    loadProject();
  }, [shareCode]);
  
  const loadProject = async () => {
    try {
      setLoading(true);
      const data = await api.getProjectByShareCode(shareCode);
      setProject(data);
    } catch (error) {
      toast.error('Error loading project');
      console.error('Error:', error);
      navigate('/access');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading project...</p>
        </div>
      </div>
    );
  }
  
  if (!project) {
    return null;
  }
  
  const statusColors = {
    draft: 'bg-amber-100 text-amber-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
  };
  
  return (
    <div className="min-h-screen bg-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/access')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {project.name}
                  </h1>
                  <Badge className={statusColors[project.status]}>
                    {project.status.replace('_', ' ')}
                  </Badge>
                </div>
                
                <div className="flex items-center space-x-3 text-sm text-slate-600 dark:text-slate-400 mt-1">
                  <span className="flex items-center">
                    <Eye className="h-3 w-3 mr-1" />
                    View Only
                  </span>
                  {project.address && (
                    <span>• {project.address}</span>
                  )}
                  <span>• Code: {shareCode}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-2xl grid-cols-3 mb-8">
            <TabsTrigger value="overview" className="flex items-center">
              <FileText className="mr-2 h-4 w-4" />
              Project Overview
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center">
              <Upload className="mr-2 h-4 w-4" />
              Project Files ({project.files?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="comments" className="flex items-center">
              <MessageSquare className="mr-2 h-4 w-4" />
              Comments ({project.comments?.length || 0})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <ProjectInfoViewer project={project} />
          </TabsContent>
          
          <TabsContent value="files">
            <ProjectFilesViewer 
              project={project} 
              onUpdate={loadProject}
              shareCode={shareCode}
            />
          </TabsContent>
          
          <TabsContent value="comments">
            <CommentsViewer
              project={project}
              onUpdate={loadProject}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SharedProjectView;